import { z } from 'zod'

import { dateUtil, isNull } from '../../util'
import { csvColumnLabelFor } from '../csvColumnLabel'

export const millsheetCsvColumnMapper = {
  constructionName: '工事名称',
  constructionArea: '工区',
  member: '節',
  buildingPart: '部位',
  steelType: '規格',
  sectionType: '形状',
  manufacturerName: 'メーカー',
  tradingCompany: '商社',
  issuedDate: '発行日',
  no: '証明書番号',
  orderNumber: '注文番号',
  innerNo: '内部番号',
  customer: '需要家',
  size: '寸法',
  thicknessDiameter: '厚・径',
  endorsementCompany: '裏書会社',
  endorsementDate: '裏書日',
  length: '長さ',
  quantity: '数量',
  weightBeforeProcessing: '重量',
  // weightAfterProcessing: '加工後重量',
  heatNumber: '製鋼番号',
  individualNumber: '個体番号',
}

// value は CSVレコードの生値（string | number | null）または zod パース後の文字列のみで、
// オブジェクトが渡ることはない。unknown のままだと String(value) が
// no-base-to-string に引っかかるため、実際に渡り得る型まで狭める
function formatValidationMessage({
  label,
  reason,
  value,
}: {
  label: string
  reason: string
  value: string | number | null | undefined
}) {
  const normalizedReason = reason.endsWith('。') ? reason : `${reason}。`
  const inputValue = isNull(value) ? '' : String(value)
  const inputValueMessage = inputValue.trim() === '' ? '' : `（入力値: ${inputValue}）`
  return `${label}: ${normalizedReason}${inputValueMessage}`
}

export function validateAndNormalizeMillsheetCsvRecords({
  record,
  index,
}: {
  record: Record<string, string | number | null>
  index: number
}) {
  // すべて空行だったらスキップ
  if (Object.values(record).every((x) => x === '')) {
    return {
      type: 'rebar' as const,
      success: false as const,
      isEmptyLine: true,
      message: `空行です。`,
      record,
      index,
    }
  }

  // TODO: keysのvalidation

  // 必要なデータのみを一旦抽出する
  const data = {
    manufacturerName: record[millsheetCsvColumnMapper.manufacturerName],
    tradingCompany: record[millsheetCsvColumnMapper.tradingCompany],
    customer: record[millsheetCsvColumnMapper.customer],
    constructionArea: record[millsheetCsvColumnMapper.constructionArea],
    member: record[millsheetCsvColumnMapper.member],
    buildingPart: record[millsheetCsvColumnMapper.buildingPart],
    constructionName: record[millsheetCsvColumnMapper.constructionName],
    issuedDate: record[millsheetCsvColumnMapper.issuedDate],
    no: record[millsheetCsvColumnMapper.no],
    orderNumber: record[millsheetCsvColumnMapper.orderNumber],
    innerNo: record[millsheetCsvColumnMapper.innerNo],
    steelType: record[millsheetCsvColumnMapper.steelType],
    sectionType: record[millsheetCsvColumnMapper.sectionType],
    size: record[millsheetCsvColumnMapper.size],
    thicknessDiameter: record[millsheetCsvColumnMapper.thicknessDiameter],
    endorsementCompany: record[millsheetCsvColumnMapper.endorsementCompany],
    endorsementDate: record[millsheetCsvColumnMapper.endorsementDate],
    length: record[millsheetCsvColumnMapper.length],
    quantity: record[millsheetCsvColumnMapper.quantity],
    weightBeforeProcessing: record[millsheetCsvColumnMapper.weightBeforeProcessing],
    // weightAfterProcessing: record[millsheetCsvColumnMapper.weightAfterProcessing],
    heatNumber: record[millsheetCsvColumnMapper.heatNumber],
    individualNumber: record[millsheetCsvColumnMapper.individualNumber],
  }
  // schemaもハードコーディング
  const schema = z.object({
    manufacturerName: z.string(),
    tradingCompany: z.string().optional(),
    customer: z.string().optional(),
    constructionArea: z.string().optional(),
    member: z.string().optional(),
    buildingPart: z.string().optional(),
    constructionName: z.string().optional(),
    issuedDate: z.string(),
    // 証明書番号は「工事名称 × 証明書番号」でミルシートを一意識別するキーの一部であり、
    // 未入力だと一意化が破綻する（本番データにも未入力は存在しない）。そのため必須とし、
    // 未入力・空欄（前後空白のみを含む）は受け付けない。null/undefined も空文字に寄せて弾く。
    no: z.preprocess((v) => v ?? '', z.string().trim().min(1, { message: '必須項目です。' })),
    orderNumber: z.string().optional(),
    innerNo: z.string().optional(),
    steelType: z.string(),
    sectionType: z.string(),
    size: z.string(),
    // 後方互換のため optional。未指定または空文字なら null として扱い、BPO が後から手入力する想定
    thicknessDiameter: z
      .string()
      .optional()
      .transform((x) => {
        if (isNull(x)) {
          return null
        }
        const trimmed = x.trim()
        return trimmed === '' ? null : trimmed
      }),
    // 裏書会社（スポット品にのみ存在）。後方互換のため optional
    endorsementCompany: z
      .string()
      .optional()
      .transform((x) => {
        if (isNull(x)) {
          return null
        }
        const trimmed = x.trim()
        return trimmed === '' ? null : trimmed
      }),
    // 裏書日。後方互換のため optional。空欄なら null、形式エラーは下流で個別にハンドリング
    endorsementDate: z.string().optional(),
    length: z.string().optional(),
    // quantityは数値の文字列（カンマ、小数点を許す）
    quantity: z.string().transform((x, ctx) => {
      if (x.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: '必須項目です。',
        })
        return z.NEVER
      }
      if (!/^[0-9,.]+$/.test(x)) {
        ctx.addIssue({
          code: 'custom',
          message: '数値で入力してください。',
        })
        return z.NEVER
      }
      return Number(x.replace(/,/g, ''))
    }),
    weightBeforeProcessing: z
      .string()
      .transform((x, ctx) => {
        const trimmed = x.trim()
        if (trimmed === '') {
          return null
        }
        if (!/^[0-9,]+$/.test(trimmed)) {
          ctx.addIssue({
            code: 'custom',
            message: '整数で入力してください。',
          })
          return z.NEVER
        }
        const parsed = Number(trimmed.replace(/,/g, ''))
        if (!Number.isInteger(parsed)) {
          ctx.addIssue({
            code: 'custom',
            message: '整数で入力してください。',
          })
          return z.NEVER
        }
        return parsed
      })
      .nullable(),
    // weightAfterProcessing: z
    //   .string()
    //   .regex(/^[0-9,.]*$/, { message: '数値で入力してください。' })
    //   .optional()
    //   .transform((x) => {
    //     if (isNull(x) || !isPresent(x)) {
    //       return undefined
    //     }
    //     return Number(x.replace(/,/g, ''))
    //   }),
    heatNumber: z.string(),
    individualNumber: z.string().optional(),
  })
  const parsed = schema.safeParse(data)

  if (!parsed.success) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: parsed.error.issues
        .map((x) => {
          const label = csvColumnLabelFor(millsheetCsvColumnMapper, x.path.join(''))
          const value = data[x.path.join('') as keyof typeof data]
          const reason = x.code === 'invalid_type' && isNull(value) ? '必須項目です。' : x.message
          return formatValidationMessage({ label, reason, value })
        })
        .join(' '),
      record,
      index,
    }
  }

  const dateParsed = dateUtil.parseDateFormatString(parsed.data.issuedDate)
  if (isNull(dateParsed)) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: formatValidationMessage({
        label: millsheetCsvColumnMapper.issuedDate,
        reason: 'YYYY/MM/DD形式で入力してください。',
        value: parsed.data.issuedDate,
      }),
      record,
      index,
    }
  }

  // 裏書日は optional。値があり、かつ形式不正なら明示的にエラー扱い
  const endorsementDateRaw = parsed.data.endorsementDate?.trim() ?? ''
  const endorsementDateParsed = endorsementDateRaw === '' ? null : dateUtil.parseDateFormatString(endorsementDateRaw)
  if (endorsementDateRaw !== '' && isNull(endorsementDateParsed)) {
    return {
      type: 'rebar' as const,
      success: false as const,
      message: formatValidationMessage({
        label: millsheetCsvColumnMapper.endorsementDate,
        reason: 'YYYY/MM/DD形式で入力してください。',
        value: endorsementDateRaw,
      }),
      record,
      index,
    }
  }

  const result = {
    type: 'rebar' as const,
    ...parsed.data,
    rank: index + 1,
    issuedDate: dateParsed.date,
    endorsementDate: endorsementDateParsed?.date ?? null,
    raw: data,
  }

  return {
    type: 'rebar' as const,
    success: true as const,
    record,
    index,
    result,
    resultTitle: result.heatNumber,
  }
}
